import type { Resultado } from './acesso';
import type { Sessao } from './auth';
import { chamar } from './encomendas-fluxo';
import type { ItemLigado } from './encomendas-fluxo';

/**
 * CENTRAL DE NOTAS (dono, 14/10/2026) — cliente HTTP do Recebimento: notas a caminho, que
 * chegaram (bipou) e que deram entrada no HOS, a nota aberta e a linha do tempo do produto.
 * Valor só vem para quem tem `notas.ver_valores` (o servidor decide; aqui chega `null`).
 */

export type SituacaoDaNota = 'A_CAMINHO' | 'NA_LOJA' | 'ENTRADA' | 'CANCELADA';
export type AbaDasNotas = SituacaoDaNota | 'CHEGARAM' | 'TODAS';

export interface NotaDaLista {
  readonly chave: string;
  readonly numero: string | null;
  readonly serie: string | null;
  readonly fornecedor: string | null;
  readonly cnpj: string | null;
  readonly emissao: string | null;
  readonly valorTotalDaNota: number | null;
  readonly cancelada: boolean;
  readonly sefazEm: string | null;
  readonly pedido: string | null;
  readonly itens: number | null;
  readonly bonificados: number;
  readonly bonificadosResumo: string | null;
  readonly bipadaEm: string | null;
  readonly bipadaPor: string | null;
  readonly hosConferenciaEm: string | null;
  readonly hosEntradaEm: string | null;
  readonly hosOperador: string | null;
  readonly conferidaEm: string | null;
  readonly conferidaPor: string | null;
  readonly itensDeEncomenda: number;
  readonly situacao: SituacaoDaNota;
  readonly diasDesdeEmissao: number | null;
}

export interface Contagem {
  readonly A_CAMINHO: number;
  readonly NA_LOJA: number;
  readonly ENTRADA: number;
  readonly CANCELADA: number;
  readonly bonificacao: number;
  readonly semBipe: number;
  readonly atrasadas: number;
}

export interface ListaDeNotas {
  readonly notas: readonly NotaDaLista[];
  readonly total: number;
  readonly contagem: Contagem;
  readonly atualizadoEm: string | null;
  readonly veValores: boolean;
  readonly avisoDoProduto: string | null;
}

export interface FiltrosDasNotas {
  readonly aba: AbaDasNotas;
  readonly q: string;
  readonly fornecedor: string;
  readonly produto: string;
  readonly de: string;
  readonly ate: string;
  readonly bonificacao: boolean;
  readonly comEncomenda: boolean;
  readonly semBipe: boolean;
  readonly valorMin: string;
  readonly valorMax: string;
}

export const FILTROS_VAZIOS: FiltrosDasNotas = {
  aba: 'A_CAMINHO',
  q: '',
  fornecedor: '',
  produto: '',
  de: '',
  ate: '',
  bonificacao: false,
  comEncomenda: false,
  semBipe: false,
  valorMin: '',
  valorMax: '',
};

/** Quantos filtros (além da aba) estão ligados — o selo do botão "Filtros". */
export function filtrosLigados(f: FiltrosDasNotas): number {
  return [
    f.q.trim(),
    f.fornecedor.trim(),
    f.produto.trim(),
    f.de,
    f.ate,
    f.bonificacao,
    f.comEncomenda,
    f.semBipe,
    f.valorMin,
    f.valorMax,
  ].filter(Boolean).length;
}

export function consultaDasNotas(f: FiltrosDasNotas, offset = 0, limite = 50): string {
  const p = new URLSearchParams();
  if (f.aba !== 'TODAS') p.set('situacao', f.aba);
  if (f.q.trim()) p.set('q', f.q.trim());
  if (f.fornecedor.trim()) p.set('fornecedor', f.fornecedor.trim());
  if (f.produto.trim()) p.set('produto', f.produto.trim());
  if (f.de) p.set('de', f.de);
  if (f.ate) p.set('ate', f.ate);
  if (f.bonificacao) p.set('bonificacao', '1');
  if (f.comEncomenda) p.set('comEncomenda', '1');
  if (f.semBipe) p.set('semBipe', '1');
  const vMin = Number(f.valorMin.replace(/\./g, '').replace(',', '.'));
  const vMax = Number(f.valorMax.replace(/\./g, '').replace(',', '.'));
  if (f.valorMin.trim() && Number.isFinite(vMin)) p.set('valorMin', String(vMin));
  if (f.valorMax.trim() && Number.isFinite(vMax)) p.set('valorMax', String(vMax));
  p.set('limite', String(limite));
  if (offset) p.set('offset', String(offset));
  return p.toString();
}

export const listarNotas = (f: FiltrosDasNotas, offset = 0): Promise<Resultado<ListaDeNotas>> =>
  chamar<ListaDeNotas>(`/api/encomendas/notas?${consultaDasNotas(f, offset)}`);

export const atualizarNotas = (sessao: Sessao) =>
  chamar<{ novas: number; entradasHos: number; erros: number }>(
    '/api/encomendas/notas/sincronizar',
    { metodo: 'POST', sessao },
  );

export type EtapaDaNota =
  | 'EMITIDA'
  | 'SEFAZ'
  | 'EM_CONFERENCIA_HOS'
  | 'BIPOU'
  | 'ENTRADA_HOS'
  | 'CONFERIDA_HOS'
  | 'CONFERIDA_INOVAPED';

export interface PassoDaLinhaDoTempo {
  readonly etapa: EtapaDaNota;
  readonly rotulo: string;
  readonly em: string | null;
  readonly quem: string | null;
  readonly detalhe: string | null;
  readonly feito: boolean;
}

export interface ItemDaNota {
  readonly linha: string | null;
  readonly codigoHos: string | null;
  readonly ean: string | null;
  readonly descricao: string | null;
  readonly quantidade: number | null;
  readonly unidade: string | null;
  readonly bonificacao: boolean;
  readonly cfop: string | null;
  readonly lote: string | null;
  readonly validade: string | null;
  readonly pmc: number | null;
  readonly custoUnitario: number | null;
  readonly custoTotal: number | null;
  readonly conferidoNoHos: number | null;
  /** A quantidade da nota segundo o HOS (mesma unidade da contagem). */
  readonly quantidadeNoHos: number | null;
  readonly deEncomenda: boolean;
}

export interface ParcelaDoBoleto {
  readonly parcela: string;
  readonly vence: string | null;
  readonly valorParcela: number | null;
  readonly pago: boolean;
  readonly pagoEm: string | null;
}

export interface NotaCompleta {
  readonly nota: NotaDaLista;
  readonly linhaDoTempo: readonly PassoDaLinhaDoTempo[];
  readonly itens: readonly ItemDaNota[];
  readonly itensDisponiveis: boolean | null;
  readonly fontes: {
    readonly inovaped: { readonly ok: boolean; readonly mensagem?: string };
    readonly hos: { readonly ok: boolean; readonly mensagem?: string };
  };
  readonly ligados: readonly ItemLigado[];
  readonly boletos: {
    readonly ok: boolean;
    readonly mensagem?: string | null;
    readonly parcelas: readonly ParcelaDoBoleto[];
    readonly bloqueio?: { readonly motivo: string | null; readonly por: string | null } | null;
  } | null;
  readonly veValores: boolean;
}

export const abrirNota = (chave: string) => chamar<NotaCompleta>(`/api/encomendas/notas/${chave}`);

export interface EventoDoProduto {
  readonly tipo: 'PEDIDO' | 'NOTA' | 'ENTRADA_HOS' | 'EM_CONFERENCIA_HOS' | 'ENCOMENDA';
  readonly em: string | null;
  readonly titulo: string;
  readonly detalhe: string | null;
  readonly quantidade: number | null;
  readonly custoUnitario: number | null;
  readonly bonificacao: boolean;
  readonly chave: string | null;
  readonly situacao: string | null;
}

export interface LinhaDoTempoDoProduto {
  readonly codigo: string;
  readonly descricao: string | null;
  readonly eventos: readonly EventoDoProduto[];
  readonly aCaminho: number;
  readonly fontes: {
    readonly inovaped: { readonly ok: boolean; readonly mensagem?: string };
    readonly hos: { readonly ok: boolean; readonly mensagem?: string };
  };
  readonly veValores: boolean;
}

export const linhaDoTempoDoProduto = (codigo: string, ean: string | null) =>
  chamar<LinhaDoTempoDoProduto>(
    `/api/encomendas/notas/produto/${codigo}${ean ? `?ean=${ean}` : ''}`,
  );

/* ---------------- formatação ---------------- */

export const ROTULO_DA_SITUACAO: Readonly<Record<SituacaoDaNota, string>> = {
  A_CAMINHO: 'A caminho',
  NA_LOJA: 'Chegou · falta entrada',
  ENTRADA: 'Deu entrada',
  CANCELADA: 'Cancelada',
};

export function diaBr(iso: string | null): string {
  if (!iso) return '—';
  if (/^\d{4}-\d{2}-\d{2}$/.test(iso)) return iso.split('-').reverse().join('/');
  return new Date(iso).toLocaleDateString('pt-BR', { timeZone: 'America/Sao_Paulo' });
}

/** "há 3 dias" / "hoje" / "ontem" — para a nota a caminho. */
export function haQuantoTempo(dias: number | null): string {
  if (dias === null) return '';
  if (dias === 0) return 'hoje';
  if (dias === 1) return 'ontem';
  return `há ${String(dias)} dias`;
}

/** Vencimento do boleto: "vence hoje", "venceu há 2 dias", "vence em 5 dias". */
export function vencimento(
  vence: string | null,
  hoje = new Date(),
): { texto: string; tom: 'ok' | 'atencao' | 'alerta' } {
  if (!vence) return { texto: 'sem vencimento', tom: 'ok' };
  const d = Math.round(
    (Date.parse(`${vence.slice(0, 10)}T12:00:00-03:00`) -
      Date.parse(
        `${hoje.toLocaleDateString('sv-SE', { timeZone: 'America/Sao_Paulo' })}T12:00:00-03:00`,
      )) /
      86_400_000,
  );
  if (d === 0) return { texto: 'vence hoje', tom: 'alerta' };
  if (d < 0) return { texto: `venceu há ${String(-d)} dia${d === -1 ? '' : 's'}`, tom: 'alerta' };
  if (d <= 3) return { texto: `vence em ${String(d)} dia${d === 1 ? '' : 's'}`, tom: 'atencao' };
  return { texto: `vence em ${String(d)} dias`, tom: 'ok' };
}
